import { useEffect, useCallback, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { io, Socket } from 'socket.io-client';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { apiClient, testUserSocketAuth } from '../client';
import { queryKeys } from '../queryKeys';
import {
  getIdToken,
  isFirebaseConfigured,
  waitForFirebaseAuthReady,
} from '@/lib/firebase';
import { useAuthStore } from '@/stores/auth-store';
import { useWarehouseStore } from '@/stores/warehouse-store';
import { resolveSystemNotificationPath } from '@/lib/notification-links';

/** Minimal shape of a system (non-chat) notification pushed over the socket. */
interface SystemNotificationPayload {
  id: string;
  type: string;
  priority: 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
  title: string;
  message: string;
  warehouseId?: string;
  entityType?: string;
  entityId?: string;
  recipientId?: string;
}

/** Minimal shape of a chat message pushed over the socket (`new_message`). */
interface ChatMessagePayload {
  id: string;
  content: string;
  authorId: string;
  channelId?: string;
  author?: { id: string; name: string };
}

// ============================================================================
// Types
// ============================================================================

export type ChatNotificationType =
  | 'MENTION'
  | 'CHANNEL_INVITE'
  | 'MESSAGE_REPLY';

export interface ChatNotificationActor {
  id: string;
  name: string;
  email?: string;
}

export interface ChatNotification {
  id: string;
  type: ChatNotificationType;
  title: string;
  body: string;
  read: boolean;
  readAt?: string;
  createdAt: string;
  userId: string;
  actor?: ChatNotificationActor;
  actorId?: string;
  messageId?: string;
  channelId?: string;
  purchaseOrderId?: string;
  salesOrderId?: string;
  // Additional context for navigation
  channel?: {
    id: string;
    name: string;
  };
  purchaseOrder?: {
    id: string;
    poNumber: string;
  };
  salesOrder?: {
    id: string;
    orderNumber: string;
  };
}

export interface ChatNotificationsResponse {
  notifications: ChatNotification[];
  total: number;
  unreadCount: number;
}

export interface UseChatNotificationsReturn {
  notifications: ChatNotification[];
  unreadCount: number;
  markAsRead: (notificationIds: string[]) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

export interface ChatNotificationFilters {
  type?: ChatNotificationType;
  read?: boolean;
  skip?: number;
  take?: number;
}

// ============================================================================
// Constants
// ============================================================================

// Derive WebSocket URL from API URL
const API_URL = import.meta.env.VITE_API_URL || '';
const WS_URL = API_URL
  ? API_URL.replace(/^http/, 'ws')
  : `ws://${window.location.host}`;

// Test user ID for development - David Pollak's actual ID
const TEST_USER_ID =
  import.meta.env.VITE_TEST_USER_ID || 'ff32838f-5809-4a7d-b844-8ad8415e6be2';

const chatNotificationKeys = queryKeys.chatNotifications;

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook for chat notifications with real-time updates via WebSocket
 *
 * Handles MENTION, CHANNEL_INVITE, and MESSAGE_REPLY notification types.
 * Listens for real-time updates and provides mutations for marking as read.
 *
 * @param filters - Optional filters for notification type, read status, pagination
 * @returns Notifications state and functions
 */
export function useChatNotifications(
  filters?: ChatNotificationFilters,
): UseChatNotificationsReturn {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const socketRef = useRef<Socket | null>(null);

  // Fetch notifications via REST API
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: chatNotificationKeys.list(filters),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.type) params.append('type', filters.type);
      if (filters?.read !== undefined)
        params.append('read', String(filters.read));
      if (filters?.skip) params.append('skip', String(filters.skip));
      if (filters?.take) params.append('take', String(filters.take));

      const queryString = params.toString();
      const endpoint = queryString
        ? `/api/chat/notifications?${queryString}`
        : '/api/chat/notifications';

      const { data: responseData } =
        await apiClient.get<ChatNotificationsResponse>(endpoint);
      return responseData;
    },
    // Refresh every 60 seconds as fallback
    refetchInterval: 60000,
    // Keep data while refetching
    staleTime: 30000,
  });

  // Mark notifications as read mutation
  const markAsReadMutation = useMutation({
    mutationFn: async (notificationIds: string[]) => {
      await apiClient.post('/api/chat/notifications/mark-read', {
        notificationIds,
      });
    },
    onSuccess: () => {
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: chatNotificationKeys.all });
      // Also refresh the unified bell badge, whose chat count comes from the
      // notifications `combinedUnreadCount` query (distinct key space).
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
    },
    // Optimistic update
    onMutate: async (notificationIds) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        queryKey: chatNotificationKeys.list(filters),
      });

      // Snapshot previous value
      const previousData = queryClient.getQueryData<ChatNotificationsResponse>(
        chatNotificationKeys.list(filters),
      );

      // Optimistically update
      if (previousData) {
        queryClient.setQueryData<ChatNotificationsResponse>(
          chatNotificationKeys.list(filters),
          {
            ...previousData,
            notifications: previousData.notifications.map((n) =>
              notificationIds.includes(n.id)
                ? { ...n, read: true, readAt: new Date().toISOString() }
                : n,
            ),
            unreadCount: Math.max(
              0,
              previousData.unreadCount - notificationIds.length,
            ),
          },
        );
      }

      return { previousData };
    },
    onError: (_, __, context) => {
      // Rollback on error
      if (context?.previousData) {
        queryClient.setQueryData(
          chatNotificationKeys.list(filters),
          context.previousData,
        );
      }
    },
  });

  // Mark all notifications as read mutation
  const markAllAsReadMutation = useMutation({
    mutationFn: async () => {
      await apiClient.post('/api/chat/notifications/mark-all-read');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: chatNotificationKeys.all });
      // Also refresh the unified bell badge (combinedUnreadCount key space).
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
    },
    // Optimistic update
    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: chatNotificationKeys.list(filters),
      });

      const previousData = queryClient.getQueryData<ChatNotificationsResponse>(
        chatNotificationKeys.list(filters),
      );

      if (previousData) {
        const now = new Date().toISOString();
        queryClient.setQueryData<ChatNotificationsResponse>(
          chatNotificationKeys.list(filters),
          {
            ...previousData,
            notifications: previousData.notifications.map((n) => ({
              ...n,
              read: true,
              readAt: n.readAt || now,
            })),
            unreadCount: 0,
          },
        );
      }

      return { previousData };
    },
    onError: (_, __, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          chatNotificationKeys.list(filters),
          context.previousData,
        );
      }
    },
  });

  // Connect to WebSocket for real-time notification updates
  useEffect(() => {
    let cancelled = false;
    let activeSocket: Socket | null = null;

    const connect = async () => {
      const { wmsToken, wmsTokenExpiresAt } = useAuthStore.getState();
      const wmsValid =
        !!wmsToken &&
        (!wmsTokenExpiresAt ||
          new Date(wmsTokenExpiresAt).getTime() > Date.now());

      const isDevNoFirebase = import.meta.env.DEV && !isFirebaseConfigured();

      let token: string | null = null;
      if (wmsValid && wmsToken) {
        token = wmsToken;
      } else if (isDevNoFirebase) {
        // Dev test-user path: no token; gateway recognizes the userId below.
      } else {
        await waitForFirebaseAuthReady();
        if (cancelled) return;
        token = await getIdToken();
      }
      if (cancelled) return;
      if (!token && !isDevNoFirebase) return;

      // Only send the dev test-user id on the dev-no-firebase path. Sending
      // it unconditionally let a deployed client whose token failed
      // verification fall back to a hardcoded (possibly nonexistent) user on
      // the gateway's dev passthrough — binding the socket to a ghost user.
      const auth: Record<string, string> = {};
      if (token) {
        auth.token = token;
      } else if (isDevNoFirebase) {
        // Gateway expects 'userId' (+ the secret when the API requires one).
        Object.assign(auth, testUserSocketAuth(TEST_USER_ID));
      }

      const socket = io(`${WS_URL}/chat`, {
        auth,
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
      });
      activeSocket = socket;

      socket.on('connect', () => {
        // Join user's personal notification room
        socket.emit('join_notifications');
      });

      // Listen for new mention notifications
      socket.on('mention_notification', (notification: ChatNotification) => {
        // Add to notifications list and update count
        queryClient.setQueryData<ChatNotificationsResponse>(
          chatNotificationKeys.list(filters),
          (oldData) => {
            if (!oldData) {
              return {
                notifications: [notification],
                total: 1,
                unreadCount: 1,
              };
            }

            // Avoid duplicates
            if (oldData.notifications.some((n) => n.id === notification.id)) {
              return oldData;
            }

            return {
              notifications: [notification, ...oldData.notifications],
              total: oldData.total + 1,
              unreadCount: oldData.unreadCount + 1,
            };
          },
        );
        toast(notification.title, {
          description: notification.body,
          action: {
            label: 'View',
            onClick: () => navigate(getNotificationPath(notification)),
          },
        });
      });

      // Listen for channel invite notifications
      socket.on(
        'channel_invite_notification',
        (notification: ChatNotification) => {
          queryClient.setQueryData<ChatNotificationsResponse>(
            chatNotificationKeys.list(filters),
            (oldData) => {
              if (!oldData) {
                return {
                  notifications: [notification],
                  total: 1,
                  unreadCount: 1,
                };
              }

              if (oldData.notifications.some((n) => n.id === notification.id)) {
                return oldData;
              }

              return {
                notifications: [notification, ...oldData.notifications],
                total: oldData.total + 1,
                unreadCount: oldData.unreadCount + 1,
              };
            },
          );
        },
      );

      // Listen for message reply notifications
      socket.on(
        'message_reply_notification',
        (notification: ChatNotification) => {
          queryClient.setQueryData<ChatNotificationsResponse>(
            chatNotificationKeys.list(filters),
            (oldData) => {
              if (!oldData) {
                return {
                  notifications: [notification],
                  total: 1,
                  unreadCount: 1,
                };
              }

              if (oldData.notifications.some((n) => n.id === notification.id)) {
                return oldData;
              }

              return {
                notifications: [notification, ...oldData.notifications],
                total: oldData.total + 1,
                unreadCount: oldData.unreadCount + 1,
              };
            },
          );
        },
      );

      // Listen for SYSTEM (non-chat) notifications pushed in real time.
      socket.on(
        'system_notification',
        (notification: SystemNotificationPayload) => {
          // Scope: only surface notifications this user would actually see —
          // targeted to me (or broadcast) AND in my current warehouse context.
          const myId = useAuthStore.getState().user?.id ?? TEST_USER_ID;
          if (notification.recipientId && notification.recipientId !== myId) {
            return;
          }
          const { selectedWarehouseId, isAllMode } =
            useWarehouseStore.getState();
          if (
            !isAllMode &&
            notification.warehouseId &&
            selectedWarehouseId &&
            notification.warehouseId !== selectedWarehouseId
          ) {
            return;
          }

          // Refresh the bell badge + any open notifications list live.
          queryClient.invalidateQueries({
            queryKey: queryKeys.notifications.all,
          });

          const path = resolveSystemNotificationPath(
            notification.entityType,
            notification.entityId,
          );
          const opts = {
            description: notification.message,
            action: { label: 'View', onClick: () => navigate(path) },
          };
          if (notification.priority === 'URGENT') {
            toast.error(notification.title, opts);
          } else if (notification.priority === 'HIGH') {
            toast.warning(notification.title, opts);
          } else {
            toast(notification.title, opts);
          }
        },
      );

      // Listen for new chat messages to alert the user when they are NOT
      // currently on the chat page (on the page they see the live thread).
      socket.on('new_message', (message: ChatMessagePayload) => {
        const myId = useAuthStore.getState().user?.id ?? TEST_USER_ID;
        if (message.authorId === myId) return;
        if (window.location.pathname.startsWith('/chat')) return;
        toast(`New message from ${message.author?.name ?? 'someone'}`, {
          description: message.content,
          action: { label: 'Open chat', onClick: () => navigate('/chat') },
        });
      });

      socketRef.current = socket;
    };

    connect();

    return () => {
      cancelled = true;
      activeSocket?.emit('leave_notifications');
      activeSocket?.disconnect();
      socketRef.current = null;
    };
  }, [queryClient, filters, navigate]);

  // Wrapped functions for stable references
  const markAsRead = useCallback(
    async (notificationIds: string[]) => {
      await markAsReadMutation.mutateAsync(notificationIds);
    },
    [markAsReadMutation],
  );

  const markAllAsRead = useCallback(async () => {
    await markAllAsReadMutation.mutateAsync();
  }, [markAllAsReadMutation]);

  return {
    notifications: data?.notifications || [],
    unreadCount: data?.unreadCount || 0,
    markAsRead,
    markAllAsRead,
    isLoading,
    error: error as Error | null,
    refetch,
  };
}

/**
 * Get navigation path for a notification
 * Useful for click handlers
 */
export function getNotificationPath(notification: ChatNotification): string {
  if (notification.channelId) {
    return `/chat?channel=${notification.channelId}${notification.messageId ? `&message=${notification.messageId}` : ''}`;
  }
  if (notification.purchaseOrderId) {
    return `/purchase-orders/${notification.purchaseOrderId}${notification.messageId ? `?message=${notification.messageId}` : ''}`;
  }
  if (notification.salesOrderId) {
    return `/sales-orders/${notification.salesOrderId}${notification.messageId ? `?message=${notification.messageId}` : ''}`;
  }
  return '/chat';
}

/**
 * Format notification timestamp for display
 */
export function formatNotificationTime(createdAt: string): string {
  const date = new Date(createdAt);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString();
}
